import { Download, ExternalLink, Film, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { montageApi, type VideoRow } from "../../api";
import { FilterChip, Segmented } from "../../components/ui/primitives";
import { GROUP_NAME, GROUP_OF, ME_HEAD_OF, ME_ROLE, PEOPLE, PROJECTS } from "../../data/people";
import { cn } from "../../lib/utils";
import type { ItemStatus } from "../../types";
import { exportMonthXlsx, monthLabel, monthPeople, monthRange, recentMonths } from "./month";
import { notify } from "../../lib/notify";
import { useSafeSearchParams, useUrlOptional, useUrlState } from "../../lib/urlState";

/**
 * «Видео»: что сделали люди и как оно вышло.
 *
 * Монтажёр видит свои видео, контент-мейкер — своей группы, руководитель —
 * весь отдел с тем, кто делал и из какой он группы. Кто что видит, решает
 * сервер; экран только раскладывает.
 *
 * Цифры — последний снимок поста по приложенной ссылке. Нет ссылки или пост
 * ещё не попал в снимки — прочерк, а не ноль: «не вышло» и «ноль просмотров»
 * это разные новости.
 */

const PERIODS = [
  { value: "7", label: "7 дней" },
  { value: "30", label: "30 дней" },
  { value: "90", label: "3 месяца" },
  // Итог месяца для премий (25.10.2026): вышедшие за календарный месяц.
  { value: "month", label: "Месяц" },
];

const STATUS_WORD: Record<ItemStatus, { text: string; cls: string }> = {
  assigned: { text: "не сдано", cls: "text-muted-foreground" },
  uploaded: { text: "у модератора", cls: "text-review-fg" },
  rework: { text: "на доработке", cls: "text-rework-fg" },
  moderated: { text: "ждёт приёмки", cls: "text-review-fg" },
  approved: { text: "ждёт ссылки", cls: "text-publish-fg" },
  published: { text: "вышло", cls: "text-done-fg" },
  cancelled: { text: "снято", cls: "text-muted-foreground" },
};

const num = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("ru-RU"));
const sum = (rows: VideoRow[], key: "views" | "likes" | "comments" | "shares" | "saved") =>
  rows.some((r) => r.stats?.[key] != null)
    ? rows.reduce((acc, r) => acc + (r.stats?.[key] ?? 0), 0)
    : null;
const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short", timeZone: "Asia/Almaty" }) : "—";

function daysAgo(days: number): string {
  const d = new Date(Date.now() + 5 * 3600e3 - days * 86400e3);
  return d.toISOString().slice(0, 10);
}

export function Videos() {
  const [params, setParams] = useSafeSearchParams();
  const summaryView = params.get("view") === "accounts" ? "accounts" : "editors";
  // Период, месяц и человек — в адресе, как направление (02.11.2026); период запомнен.
  const [period, setPeriod] = useUrlState("period", "30", { remember: "montage:videos:period" });
  const months = useMemo(() => recentMonths(), []);
  const [monthRaw, setMonth] = useUrlState("month", months[0]);
  const month = months.includes(monthRaw) ? monthRaw : months[0];
  const monthly = period === "month";
  const [rows, setRows] = useState<VideoRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const group = params.get("direction");
  const setGroup = (projectId: string | null) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (projectId) next.set("direction", projectId);
      else next.delete("direction");
      next.delete("branch");
      return next;
    }, { replace: true });
  };
  const setSummaryView = (view: string) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (view === "accounts") next.set("view", "accounts");
      else next.delete("view");
      return next;
    }, { replace: true });
  };
  const [person, setPerson] = useUrlOptional("person");
  // Колонка «Взяли» — руководителю: кто из других отделов взял ролик себе.
  const [takesVisible, setTakesVisible] = useState(false);
  const [takenOnly, setTakenOnly] = useState(false);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    setStatsError(null);
    const [from, to] = monthly ? monthRange(month) : [daysAgo(Number(period)), undefined];
    montageApi.videos(from, to, monthly ? "published" : undefined)
      .then((r) => { if (alive) { setRows(r.videos); setTakesVisible(Boolean(r.takesVisible)); setStatsError(r.statsError ?? null); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [period, month, monthly, reload]);

  const all = rows ?? [];
  const groups = useMemo(() => {
    const map = new Map<string, string>();
    // Все доступные направления, включая те, где за период ещё нет роликов.
    for (const project of PROJECTS) map.set(project.id, project.name);
    for (const r of all) if (r.project) map.set(r.project.id, r.project.name);
    return [...map].map(([id, name]) => ({ id, name }));
  }, [all]);
  const selectedProject = PROJECTS.find((project) => project.id === group);
  const branches = selectedProject?.name === "Президент"
    ? selectedProject.groupIds.filter((id) => /^(Алматы|Алмата|Астана)$/i.test(GROUP_NAME[id] ?? ""))
    : [];
  const branch = branches.includes(params.get("branch") ?? "") ? params.get("branch") : null;
  const setBranch = (id: string | null) => {
    setPerson(null);
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (id) next.set("branch", id);
      else next.delete("branch");
      return next;
    }, { replace: true });
  };
  const byTaken = takenOnly ? all.filter((r) => r.takes?.length) : all;
  const byProject = group ? byTaken.filter((r) => r.project?.id === group) : byTaken;
  const byGroup = branch ? byProject.filter((r) => (r.group?.id ?? GROUP_OF[r.editor.id]) === branch) : byProject;
  const shown = person ? byGroup.filter((r) => r.editor.id === person) : byGroup;

  // Сводка по людям: сколько сделал, сколько вышло, сколько это набрало.
  const people = useMemo(() => {
    const map = new Map<string, { id: string; name: string; group: string | null; rows: VideoRow[] }>();
    for (const r of byGroup) {
      const p = map.get(r.editor.id) ?? { id: r.editor.id, name: r.editor.name, group: r.project?.name ?? null, rows: [] };
      p.rows.push(r);
      map.set(r.editor.id, p);
    }
    return [...map.values()].sort((a, b) => (sum(b.rows, "views") ?? 0) - (sum(a.rows, "views") ?? 0) || b.rows.length - a.rows.length);
  }, [byGroup]);
  const accounts = useMemo(() => {
    const map = new Map<string, { id: string; name: string; rows: VideoRow[] }>();
    for (const row of byGroup) {
      const account = row.account ?? { id: "unassigned", name: "Аккаунт не указан" };
      const current = map.get(account.id) ?? { ...account, rows: [] };
      current.rows.push(row);
      map.set(account.id, current);
    }
    return [...map.values()].sort((a, b) => (sum(b.rows, "views") ?? 0) - (sum(a.rows, "views") ?? 0) || b.rows.length - a.rows.length);
  }, [byGroup]);

  const seeAll = ME_HEAD_OF.length > 0;
  // Контент-мейкер направления — для подписи «Akorda News · Дархан».
  const makerOf = (projectId: string) => {
    const id = PROJECTS.find((p) => p.id === projectId)?.makerIds?.[0];
    return id ? PEOPLE[id].name : null;
  };
  const solo = ME_ROLE === "editor";
  const published = shown.filter((r) => r.status === "published").length;
  // Сводка «взяли» за период: сколько роликов и кто сколько.
  const takenRows = shown.filter((r) => r.takes?.length);
  const takers = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of takenRows) for (const t of r.takes) map.set(t.by, (map.get(t.by) ?? 0) + 1);
    return [...map].sort((a, b) => b[1] - a[1]);
  }, [takenRows]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-5">
      <header className="flex flex-wrap items-center gap-4">
        <h1 className="sr-only">Видео</h1>
        <Segmented id="videos-period" value={period} onChange={setPeriod} options={PERIODS} />
        {monthly && (
          <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Месяц" className="h-9 rounded-full border bg-background px-3 text-[13px] font-medium">
            {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        )}
        {!solo && <Segmented id="videos-summary" value={summaryView} onChange={setSummaryView} options={[
          { value: "editors", label: "По монтажёрам" },
          { value: "accounts", label: "По аккаунтам" },
        ]} />}
        <span className="flex-1" />
        <button type="button" onClick={() => setReload((value) => value + 1)} className="flex items-center gap-1.5 text-[13px] text-muted-foreground" aria-label="Обновить видео и статистику">
          <RefreshCw className="size-4" /> Обновить
        </button>
        <span className="text-[13px] text-muted-foreground">
          {solo ? "ваши видео" : seeAll ? "весь отдел" : "ваши направления"} · {shown.length} · вышло {published} · {num(sum(shown, "views"))} просмотров
        </span>
      </header>

      {((!solo && groups.length > 1) || takesVisible) && (
        <div className="flex flex-wrap items-center gap-2">
          {!solo && groups.length > 1 && (
            <>
              <FilterChip active={!group} onClick={() => { setGroup(null); setPerson(null); }}>Все направления</FilterChip>
              {groups.map((g) => (
                <FilterChip key={g.id} active={group === g.id} onClick={() => { setGroup(g.id); setPerson(null); }}>{g.name}</FilterChip>
              ))}
            </>
          )}
          {takesVisible && (
            <>
              <span className="mx-1 h-5 w-px bg-border" />
              <FilterChip active={takenOnly} onClick={() => setTakenOnly(!takenOnly)} count={all.filter((r) => r.takes?.length).length}>Взятые</FilterChip>
              <span className="text-[13px] text-muted-foreground">
                {takenRows.length === 0
                  ? "за период никто не брал"
                  : `взяли ${takenRows.length}: ${takers.map(([name, n]) => `${name} — ${n}`).join(", ")}`}
              </span>
            </>
          )}
        </div>
      )}

      {branches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Отделения проекта Президент">
          <span className="text-[13px] text-muted-foreground">Отделения:</span>
          <FilterChip active={!branch} onClick={() => setBranch(null)}>Все отделения</FilterChip>
          {branches.map((id) => (
            <FilterChip key={id} active={branch === id} onClick={() => setBranch(id)}>{GROUP_NAME[id]}</FilterChip>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-rework-fg">Не удалось загрузить: {error}</p>}
      {statsError && <p role="alert" className="text-sm text-rework-fg">{statsError}</p>}

      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pb-6">
        {monthly && rows && <MonthSummary month={month} rows={byGroup} scope={solo ? "ваши ролики" : selectedProject?.name ?? (seeAll ? "весь отдел" : "ваши направления")} onPerson={setPerson} person={person} />}

        {!solo && !monthly && summaryView === "accounts" && accounts.length > 0 && (
          <section className="space-y-2.5">
            <h2 className="text-sm font-semibold">Статистика по аккаунтам</h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
              {accounts.map((account) => (
                <div key={account.id} className="flex flex-col gap-1.5 rounded-[20px] border bg-card p-3.5">
                  <span className="truncate text-sm font-semibold">{account.name.startsWith("@") || account.id === "unassigned" ? account.name : `@${account.name}`}</span>
                  <span className="text-xs text-muted-foreground">{account.rows.length} видео · вышло {account.rows.filter((row) => row.status === "published").length}</span>
                  <span className="text-[13px] tabular-nums"><b>{num(sum(account.rows, "views"))}</b> просм. · {num(sum(account.rows, "likes"))} лайков</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {!solo && !monthly && summaryView === "editors" && people.length > 0 && (
          <section className="space-y-2.5">
            <h2 className="text-sm font-semibold">Статистика по монтажёрам</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
            {people.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPerson(person === p.id ? null : p.id)}
                className={cn(
                  "flex flex-col gap-1.5 rounded-[20px] border bg-card p-3.5 text-left transition-shadow hover:shadow-mmd",
                  person === p.id && "ring-2 ring-inset ring-primary",
                )}
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-semibold">{p.name}</span>
                  {p.group && <span className="shrink-0 text-xs text-muted-foreground">{p.group}</span>}
                </span>
                <span className="text-xs text-muted-foreground">
                  {p.rows.length} видео · вышло {p.rows.filter((r) => r.status === "published").length}
                </span>
                <span className="text-[13px] tabular-nums">
                  <b>{num(sum(p.rows, "views"))}</b> просм. · {num(sum(p.rows, "likes"))} лайков
                </span>
              </button>
            ))}
          </div>
          </section>
        )}

        {rows === null && !error ? (
          <p className="text-sm text-muted-foreground">Загружаем…</p>
        ) : shown.length === 0 ? (
          <div className="grid place-items-center gap-2 rounded-[20px] border border-dashed py-16 text-center text-sm text-muted-foreground">
            <Film className="size-6" />
            {monthly ? `В ${monthLabel(month).toLowerCase()} роликов не выходило` : "За этот период видео нет"}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[20px] border bg-card">
            <table className={cn("w-full text-[13px]", takesVisible ? "min-w-[1140px]" : "min-w-[980px]")}>
              <thead className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3.5 py-2.5 font-medium">Видео</th>
                  <th className="px-3 py-2.5 font-medium">Публикация</th>
                  {!solo && <th className="px-3 py-2.5 font-medium">Кто</th>}
                  <th className="px-3 py-2.5 font-medium">Статус</th>
                  {takesVisible && <th className="px-3 py-2.5 font-medium">Взяли</th>}
                  <th className="px-3 py-2.5 font-medium">Сдано</th>
                  <th className="px-3 py-2.5 text-right font-medium">Просмотры</th>
                  <th className="px-3 py-2.5 text-right font-medium">Лайки</th>
                  <th className="px-3 py-2.5 text-right font-medium">Комм.</th>
                  <th className="px-3 py-2.5 text-right font-medium">Репосты</th>
                  <th className="px-3 py-2.5 text-right font-medium">Сохр.</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="max-w-[260px] px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium" title={r.title}>{r.title}</span>
                        {r.link && (
                          <a href={r.link} target="_blank" rel="noreferrer" title="Открыть пост" className="shrink-0 text-muted-foreground hover:text-foreground">
                            <ExternalLink className="size-3.5" />
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{r.format}{r.versions > 1 ? ` · ${r.versions} версии` : ""}</div>
                    </td>
                    <td className="max-w-[220px] px-3 py-2.5">
                      <div className="truncate" title={r.topic.title}>{r.topic.title}</div>
                      <div className="text-xs text-muted-foreground">{r.topic.code}{r.project ? ` · ${r.project.name}` : ""}</div>
                    </td>
                    {!solo && (
                      <td className="px-3 py-2.5">
                        <div className="truncate">{r.editor.name}</div>
                        <div className="text-xs text-muted-foreground">{r.project ? `${r.project.name}${makerOf(r.project.id) ? ` · ${makerOf(r.project.id)}` : ""}` : "—"}</div>
                      </td>
                    )}
                    <td className={cn("px-3 py-2.5 font-medium", STATUS_WORD[r.status].cls)}>
                      {STATUS_WORD[r.status].text}
                      {r.publishedAt && <div className="text-xs font-normal text-muted-foreground">{shortDate(r.publishedAt)}</div>}
                    </td>
                    {takesVisible && (
                      <td className="max-w-[200px] px-3 py-2.5">
                        {r.takes?.length ? r.takes.map((t, i) => (
                          <div key={i} className="truncate" title={[t.division, t.public, t.link].filter(Boolean).join(" · ")}>
                            {t.by} · {shortDate(t.at)}
                            {t.link
                              ? <a href={t.link} target="_blank" rel="noreferrer" className="ml-1 inline-flex align-[-2px] text-muted-foreground hover:text-foreground"><ExternalLink className="size-3.5" /></a>
                              : null}
                            {t.public && <div className="truncate text-xs text-muted-foreground">{t.public}</div>}
                          </div>
                        )) : <span className="text-muted-foreground">—</span>}
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-muted-foreground">{shortDate(r.submittedAt)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{num(r.stats?.views)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{num(r.stats?.likes)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{num(r.stats?.comments)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{num(r.stats?.shares)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{num(r.stats?.saved)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Цифры — последний снимок поста по приложенной ссылке. Прочерк: ссылки нет или пост ещё не попал в снимки статистики.
        </p>
      </div>
    </div>
  );
}

/**
 * Итог месяца: по сотруднику — сколько роликов вышло и сколько набрали, и
 * общий итог. Нажатие на строку оставляет в таблице ниже ролики человека —
 * там же ссылки.
 */
function MonthSummary({ month, rows, scope, person, onPerson }: { month: string; rows: VideoRow[]; scope: string; person: string | null; onPerson: (id: string | null) => void }) {
  const people = monthPeople(rows);
  const total = people.reduce((n, p) => n + p.published, 0);
  const views = people.some((p) => p.views != null) ? people.reduce((n, p) => n + (p.views ?? 0), 0) : null;
  const [busy, setBusy] = useState(false);
  return (
    <section className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Итог · {monthLabel(month)}</h2>
        <span className="text-[13px] text-muted-foreground">{total} роликов вышло · {num(views)} просмотров</span>
        <span className="flex-1" />
        <button
          type="button"
          disabled={busy || people.length === 0}
          onClick={() => {
            setBusy(true);
            exportMonthXlsx(month, people, scope)
              .catch((e) => notify((e as Error).message, { icon: "rework" }))
              .finally(() => setBusy(false));
          }}
          className="flex items-center gap-1.5 text-[13px] font-medium underline underline-offset-2 disabled:opacity-50"
        >
          <Download className="size-4" /> Скачать Excel
        </button>
      </div>
      <div className="overflow-x-auto rounded-[20px] border bg-card">
        <table className="w-full min-w-[560px] text-[13px]">
          <thead className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3.5 py-2.5 font-medium">Сотрудник</th>
              <th className="px-3 py-2.5 font-medium">Направления</th>
              <th className="px-3 py-2.5 text-right font-medium">Роликов вышло</th>
              <th className="px-3 py-2.5 text-right font-medium">Просмотры</th>
              <th className="px-3 py-2.5 text-right font-medium">Среднее</th>
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.id} onClick={() => onPerson(person === p.id ? null : p.id)} className={cn("cursor-pointer border-b hover:bg-muted/30", person === p.id && "bg-muted/50")}>
                <td className="px-3.5 py-2.5 font-medium">{p.name}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{p.projects.join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{p.published}</td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{num(p.views)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{p.views != null ? num(Math.round(p.views / p.published)) : "—"}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="px-3.5 py-2.5">Итого</td>
              <td />
              <td className="px-3 py-2.5 text-right tabular-nums">{total}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{num(views)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{views != null && total ? num(Math.round(views / total)) : "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
